"use client";

import { RootState } from "@/store/store";
import Link from '@/compat/Link';
import { usePathname, useRouter, useSearchParams } from '@/compat/navigation';
import { useMemo, useState, Suspense, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../ui/button";
import Icon from "../ui/icon";
import SidebarShimmer from "./SidebarShimmer";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import Image from '@/compat/Image';
import { toggleSidebar } from "@/store/feature/sidebarSlice";
import { fetchDynamicMenu } from "@/store/feature/layoutConfigSlice";
import { useAuthState } from "@/hooks/useAuthState";
import { logout } from "@/store/feature/auth/authSlice";
import { getInitials } from "@/utils/getInitials";
import { useToast } from "@/contexts/ToastContext";

/** --- icons map --- */
const sectionIconMap: Record<string, string> = {
  Dashboard: "/sidebar/dashboard.svg",
  "Safety DB": "/sidebar/safety-db.svg",
  // E2B Release 3 reuses the Safety DB icon until a dedicated asset
  // lands in public/sidebar/ (TODO: replace with a distinct icon).
  "E2B Release 3": "/sidebar/safety-db.svg",
  "Literature Review": "/sidebar/literature-review.svg",
  "Signal Detection": "/sidebar/signal-detection.svg",
  Configuration: "/sidebar/configuration.svg",
  "MICC Intake": "/sidebar/Medical-Enquiry.svg",
};

// Lucide icon fallback for modules without an SVG file
const sectionLucideIconMap: Record<string, string> = {
  "Case Processing": "ClipboardList",
  "Compliance":      "ShieldCheck",
};

import { MenuItem } from "@/store/feature/layoutConfigSlice";

// Helper component for recursive menu items
const SidebarItem = ({
  item,
  level = 0,
  isToggled,
  isActive,
  activeRoute,
  toggleSubSection,
}: {
  item: MenuItem;
  level?: number;
  isToggled: boolean;
  isActive: boolean;
  activeRoute: string | null;
  toggleSubSection?: (name: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.items && item.items.length > 0;

  // Helper to check deep activity
  function checkRecursiveActive(i: MenuItem): boolean {
    if (isRouteActive(i.route, activeRoute)) return true;
    return i.items ? i.items.some((c) => checkRecursiveActive(c)) : false;
  }

  const isItemActive = hasChildren
    ? item.items!.some(
        (child) =>
          isRouteActive(child.route, activeRoute) ||
          (child.items && checkRecursiveActive(child)),
      )
    : isRouteActive(item.route, activeRoute);

  // Auto-expand if child is active
  useEffect(() => {
    if (isItemActive && hasChildren && !isOpen && !isToggled) {
      setIsOpen(true);
    }
  }, [isItemActive, isToggled, hasChildren]); // Only auto-expand, don't auto-close

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  // Render Collapsed State (Just Icon with Tooltip)
  if (isToggled) {
    // In collapsed mode, we usually only show the top-level items or flattening them?
    // User requested to "show tooltip".
    // If item has children, in collapsed mode, usually we might ignore children or show a popover.
    // For this implementation, I will behave like the previous code:
    // Just render the item itself if it's at level 0 (Module Item).
    // Nested items in collapsed menu are tricky without a popover menu.
    // I will render the item itself. If it's a parent, clicking might not do much without a route.

    if (item.disabled) {
      return (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <div
              className="flex items-center justify-center h-[40px] w-[64px] rounded-lg cursor-not-allowed opacity-50"
              aria-disabled="true"
            >
              <Icon name="Lock" className="h-4 w-4 text-white/60" />
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={8}
            className="bg-white text-gray-800 border-l-4 border-[#1E40AF]"
          >
            {item.label} — restricted in sandbox
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <Link href={item.route || "#"} aria-label={item.label}>
            <div
              className={`flex items-center justify-center h-[40px] w-[64px] rounded-lg transition-all duration-300 ease-in-out cursor-pointer
                                ${isItemActive ? "bg-white/10 border-r-2 border-[#2FBF71]" : "hover:bg-white/5"}`}
            >
              <Icon
                name={(item.icon || "CircleHelp") as Parameters<typeof Icon>[0]["name"]}
                className="h-5 w-5 shrink-0 text-white"
                aria-hidden="true"
              />
            </div>
          </Link>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          sideOffset={8}
          className="bg-white text-gray-800 border-l-4 border-[#1E40AF]"
        >
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Leaf Item — disabled (not implemented)
  if (item.disabled) {
    return (
      <div
        className="flex items-center justify-between gap-3 py-2 pr-3 text-sm text-white/40 cursor-not-allowed select-none"
        style={{ paddingLeft: `${level * 16 + 12}px` }}
        aria-disabled="true"
      >
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <span className="truncate">{item.label}</span>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={8}
            className="bg-white text-gray-800 border-l-4 border-[#1E40AF]"
          >
            {item.label} — restricted in sandbox
          </TooltipContent>
        </Tooltip>
        <Icon name="Lock" className="h-3.5 w-3.5 opacity-60 flex-shrink-0" />
      </div>
    );
  }


  // Expanded State
  if (hasChildren) {
    return (
      <div className="flex flex-col gap-1">
        <div
          className={`flex items-center cursor-pointer justify-between px-3 py-2 rounded-lg text-sm transition-all duration-200
                             ${isItemActive ? "text-white font-medium" : "text-white/80 hover:text-white hover:bg-white/5"}`}
          style={{ paddingLeft: `${level * 16 + 12}px` }}
          onClick={handleToggle}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span className="truncate">{item.label}</span>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                sideOffset={8}
                className="bg-white text-gray-800 border-l-4 border-[#1E40AF]"
              >
                {item.label}
              </TooltipContent>
            </Tooltip>
          </div>
          <div
            className="text-white flex-shrink-0 ml-2"
            style={{
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <Icon name="ChevronRight" className="h-4 w-4 opacity-70" />
          </div>
        </div>
        {isOpen && (
          <div className="flex flex-col gap-1">
            {item.items?.map((child) => (
              <SidebarItem
                key={child.label}
                item={child}
                level={level + 1}
                isToggled={isToggled}
                isActive={isActive}
                activeRoute={activeRoute}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Leaf Item
  return (
    <Button
      variant="ghost"
      asChild
      className={`justify-start h-auto py-2 font-normal text-sm transition-all duration-200
                    ${isItemActive ? "text-[#2FBF71] hover:text-[#2FBF71] hover:bg-white/5" : "text-white/80 hover:text-white hover:bg-white/5"}`}
      style={{ paddingLeft: `${level * 16 + 12}px` }}
    >
      <Link
        href={item.route || "#"}
        className="flex items-center gap-3 w-full min-w-0"
      >
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <span className="truncate">{item.label}</span>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={8}
            className="bg-white text-gray-800 border-l-4 border-[#1E40AF]"
          >
            {item.label}
          </TooltipContent>
        </Tooltip>
      </Link>
    </Button>
  );
};

// Collect every leaf route from the menu tree
const collectLeafRoutes = (items: MenuItem[]): string[] =>
  items.flatMap((item) =>
    item.items && item.items.length > 0
      ? collectLeafRoutes(item.items)
      : item.route && item.route !== "#"
        ? [item.route]
        : [],
  );

// Find the most specific route that matches the current path (longest prefix wins)
const findActiveRoute = (
  allLeafRoutes: string[],
  currentPath: string,
): string | null => {
  const matches = allLeafRoutes.filter(
    (r) => currentPath === r || currentPath.startsWith(r + "/"),
  );
  if (matches.length === 0) return null;
  return matches.sort((a, b) => b.length - a.length)[0];
};

// A route is active only when it is the best (most specific) match
const isRouteActive = (
  route: string | null | undefined,
  activeRoute: string | null,
) => {
  if (!route || route === "#" || !activeRoute) return false;
  return route === activeRoute;
};

function SidebarInner() {
  const toggled = useSelector((state: RootState) => state.sidebar.toggled);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  // When a detail page is opened from "My Work", keep "My Work" highlighted
  const effectivePath =
    searchParams.get("from") === "mywork" ? "/signals/my-work" : pathname;

  const sideNavItems = useSelector(
    (state: RootState) => state.layoutConfig.layoutConfig?.sidenav,
  ) as any[] | undefined;

  const [activeSubSection, setActiveSubSection] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { user } = useAuthState();
  const { showSuccess } = useToast();

  useEffect(() => {
    dispatch(fetchDynamicMenu() as any);
  }, [dispatch]);

  // Close user menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = () => {
    showSuccess("You've been logged out successfully.", 3000);
    setTimeout(() => {
      dispatch(logout());
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      } else {
        router.push("/login");
      }
    }, 1000);
  };

  const menuWarning = useSelector((state: RootState) => state.layoutConfig.warning);
  const isLoading = useSelector(
    (state: RootState) => state.layoutConfig.loading,
  );

  if (!sideNavItems || isLoading) return <SidebarShimmer toggled={toggled} />;

  // Collect all leaf routes and find the most specific one for the current path
  const allLeafRoutes = sideNavItems.flatMap((section: any) =>
    (section.items ?? []).flatMap((sub: any) =>
      collectLeafRoutes(
        sub.items && sub.items.length > 0
          ? sub.items
          : [{ ...sub, label: sub.name }],
      ),
    ),
  );
  const activeRoute = findActiveRoute(allLeafRoutes, effectivePath);

  const toggleSubSection = (subSectionName: string) => {
    if (!toggled) {
      setActiveSubSection((prev) =>
        prev === subSectionName ? null : subSectionName,
      );
    }
  };

  const isModuleActive = (module: any): boolean => {
    const checkItems = (items: MenuItem[]): boolean =>
      items.some(
        (i) =>
          isRouteActive(i.route, activeRoute) ||
          (i.items ? checkItems(i.items) : false),
      );
    return module.items
      ? checkItems(module.items)
      : isRouteActive(module.route, activeRoute);
  };

  return (
    <div
      className={`h-full transition-all duration-300 ease-in-out px-4 py-4 flex flex-col z-10
    ${toggled ? "w-[80px] items-center" : "w-[280px]"}`}
      style={{
        background: "linear-gradient(180deg, #1E40AF 0%, #1E3A8A 100%)",
        boxShadow: "4px 0px 20px 0px #1E40AF26",
      }}
    >
      {/* Header / Logo + Toggle Button */}
      <div className="relative flex items-center justify-center px-3 pt-6 pb-7">
        {!toggled ? (
          <Image
            src="/logo_new.svg"
            alt="TheragenX Logo"
            width={169}
            height={41}
            className="w-[169px] h-[41px] justify-center"
            priority
          />
        ) : (
          <Image
            src="/closedsidebar.svg"
            alt="TheragenX Logo"
            width={41}
            height={41}
            className="w-[41px] h-[41px]"
            priority
          />
        )}

        <button
          className={`absolute top-1/2 transform -translate-y-1/2 translate-x-1/2 z-30 flex justify-center items-center cursor-pointer
        ${toggled ? "right-[-16px]" : "right-[-16px]"}
        w-[24px] h-[24px] rounded-full border border-gray-300 bg-white hover:bg-gray-100 transition-colors shadow-sm`}
          onClick={() => dispatch(toggleSidebar())}
          aria-label={toggled ? "Open sidebar" : "Collapse sidebar"}
          title={toggled ? "Open sidebar" : "Collapse sidebar"}
        >
          <Icon name={toggled ? "ChevronRight" : "ChevronLeft"} />
        </button>
      </div>

      {menuWarning && <p role="alert" className="text-sm p-2">{menuWarning} Keeping the last valid menu settings.</p>}
      {/* Menu Items */}
      <div
        className={`flex-1 flex flex-col overflow-y-auto scrollbar-hidden transition-all duration-300 ease-in-out ${
          toggled ? "gap-1 mt-3" : "gap-4 space-y-2 mt-3"
        }`}
      >
        {sideNavItems.map((section: any) => {
          return (
            <div
              key={section.name}
              className={`flex flex-col transition-all duration-300 ease-in-out ${
                toggled ? "gap-0.5" : "gap-2"
              }`}
            >
              {/* Section divider — collapsed mode only */}
              {toggled && (
                <div className="flex items-center mb-2 h-[32px] w-[64px] justify-center rounded-lg">
                  <div className="h-px w-8 bg-white/20" />
                </div>
              )}

              {/* Sub-sections (Modules) */}
              {Array.isArray(section.items) && (
                <div
                  className={`flex flex-col ${toggled ? "gap-1" : "gap-2 ml-2"} transition-all duration-300 ease-in-out`}
                >
                  {section.items.map((subSection: any) => {
                    const isSubSectionOpen =
                      activeSubSection === subSection.name;
                    const isActive = isModuleActive(subSection);

                    if (subSection.disabled) return <SidebarItem key={subSection.id || subSection.name}
                      item={{ ...subSection, label: subSection.name }} isToggled={toggled}
                      isActive={false} activeRoute={activeRoute} />;
                    if (toggled) {
                      // Collapsed: Flatten and show all items with icons? Or just top level?
                      // Displaying just the top-level items of the module
                      if (subSection.items && subSection.items.length > 0) {
                        return subSection.items?.map((item: MenuItem) => (
                          <SidebarItem
                            key={item.label}
                            item={item}
                            isToggled={true}
                            isActive={isActive}
                            activeRoute={activeRoute}
                            level={0}
                          />
                        ));
                      } else {
                        // Render the module itself if it has no children
                        return (
                          <SidebarItem
                            key={subSection.name}
                            item={{ ...subSection, label: subSection.name }}
                            isToggled={true}
                            isActive={isActive}
                            activeRoute={activeRoute}
                            level={0}
                          />
                        );
                      }
                    }

                    // Expanded: Module Group
                    return (
                      <div
                        key={subSection.name}
                        className={`flex flex-col gap-2 rounded-lg transition-all duration-300 ease-in-out ${
                          isActive
                            ? "bg-white/10 border-r-2 border-[#2FBF71]"
                            : ""
                        }`}
                      >
                        {/* Module Wrapper */}
                        {/* Single item module or group? The current logic treats module as a group header if >1 items */}
                        {/* But now items can be recursive children. */}
                        {/* We treat subSection.name as the Header (e.g. "Configuration") */}

                        <div
                          className={`flex items-center cursor-pointer group h-[36px] rounded-lg transition-all duration-300 ease-in-out
                            w-[220px] pl-3 justify-start ${isActive ? "" : "hover:bg-white/5"}`}
                          onClick={() => {
                            if (
                              subSection.items &&
                              subSection.items.length > 0
                            ) {
                              toggleSubSection(subSection.name);
                            } else if (subSection.route) {
                              router.push(subSection.route);
                            }
                          }}
                        >
                          <div className="flex items-center gap-3 w-full">
                            {sectionIconMap[subSection.name] ? (
                              <Image
                                src={sectionIconMap[subSection.name]}
                                alt={`${subSection.name} Icon`}
                                width={20}
                                height={20}
                                className="min-w-[20px] min-h-[20px] brightness-0 invert"
                              />
                            ) : sectionLucideIconMap[subSection.name] ? (
                              <Icon
                                // Cast: sectionLucideIconMap is a Record<string,string>
                                // but Icon's name prop is keyof typeof Icons (strict
                                // union). All values in the map are valid lucide names
                                // by construction.
                                name={sectionLucideIconMap[subSection.name] as Parameters<typeof Icon>[0]["name"]}
                                className="h-5 w-5 min-w-[20px] text-white"
                              />
                            ) : null}
                            <span
                              className="text-sm font-semibold tracking-wide text-white flex-1 truncate"
                              style={{ fontSize: "14px", fontWeight: 600 }}
                            >
                              {subSection.name}
                            </span>
                            {subSection.items &&
                              subSection.items.length > 0 && (
                                <div
                                  style={{
                                    transform: isSubSectionOpen
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s",
                                  }}
                                >
                                  <Icon
                                    name="ChevronRight"
                                    className="h-4 w-4 text-white"
                                  />
                                </div>
                              )}
                          </div>
                        </div>

                        {/* Module Items (Recursive) */}
                        {isSubSectionOpen && subSection.items && (
                          <div className="flex flex-col gap-1 ml-2 transition-all">
                            {subSection.items.map((item: MenuItem) => (
                              <SidebarItem
                                key={item.label}
                                item={item}
                                level={0}
                                isToggled={false}
                                isActive={isActive}
                                activeRoute={activeRoute}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── User profile — pinned to bottom ─────────────────────── */}
      {user && (
        <div
          ref={userMenuRef}
          className="relative flex-shrink-0 border-t border-white/10 pt-3 pb-2 px-1"
        >
          <button
            onClick={() => setUserMenuOpen((o) => !o)}
            className={`w-full flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-white/10 transition-colors ${
              toggled ? "justify-center" : ""
            }`}
          >
            {/* Avatar */}
            {user.avatar ? (
              <Image
                src={user.avatar}
                alt="User"
                width={32}
                height={32}
                className="w-8 h-8 rounded-full border-2 border-white/30 flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-white/20 border border-white/30 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {getInitials(user.name)}
              </div>
            )}

            {/* Name + role — only in expanded state */}
            {!toggled && (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-xs font-semibold text-white truncate leading-tight">
                  {user.name}
                </p>
                <p className="text-[10px] text-white/50 truncate leading-tight">
                  {user.email}
                </p>
              </div>
            )}
          </button>

          {/* Dropdown — opens upward */}
          {userMenuOpen && (
            <div className="absolute bottom-full left-2 right-2 mb-1 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50 overflow-hidden">
              <button
                className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setUserMenuOpen(false);
                  router.push("/change-password");
                }}
              >
                Change Password
              </button>
              <div className="border-t border-gray-100 my-0.5" />
              <button
                className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
                onClick={() => {
                  setUserMenuOpen(false);
                  handleLogout();
                }}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  return (
    <Suspense fallback={<SidebarShimmer toggled={false} />}>
      <SidebarInner />
    </Suspense>
  );
}

export default Sidebar;
